import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import {
  useFetchEcommerceOrdersMutation,
  useFetchEcommerceOrderByIdMutation,
  useUpdateEcommerceOrderMutation,
  useReportEcommerceShortfallMutation,
  useBulkAddEcommerceOrderMutation,
} from "../../../features/tenant/ecommerceOrder/ecommerceOrderApi";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import CSVReader from "../../../components/ImportCsv/CSVReader";
import { downloadCSV } from "../../../utils/downloadCSV";
import { FiEye, FiCheckCircle, FiXCircle, FiEdit } from "react-icons/fi";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";
import { CgShoppingBag } from "react-icons/cg";

const sampleOrdersData = [
  {
    "Buyer Mobile": "9999999991",
    "Order Remark": "Urgent Delivery",
    "Article No": "ART-001",
    "Color": "Red",
    "Size": "8",
    "Item Qty": "5"
  },
  {
    "Buyer Mobile": "9999999991",
    "Order Remark": "Urgent Delivery",
    "Article No": "ART-001",
    "Color": "Red",
    "Size": "9",
    "Item Qty": "10"
  }
];

const ECOMMERCE_ORDER_STATUS = {
  REJECTED: "0",
  PENDING: "2",
  SHORTFALL_AWAITING_USER: "3",
  SHORTFALL_AUTO_APPROVED: "4",
  APPROVED: "5",
  IN_TRANSIT: "6",
  DELIVERED: "7",
  DISTRIBUTOR_PENDING: "8",
};

const getStatusInfo = (status) => {
  const s = status?.toString();
  switch (s) {
    case ECOMMERCE_ORDER_STATUS.REJECTED:
      return { label: "Rejected",             colorClass: "bg-red-100 text-red-700 border border-red-200" };
    case ECOMMERCE_ORDER_STATUS.PENDING:
      return { label: "Pending",              colorClass: "bg-yellow-100 text-yellow-700 border border-yellow-200" };
    case ECOMMERCE_ORDER_STATUS.SHORTFALL_AWAITING_USER:
      return { label: "Shortfall – Awaiting", colorClass: "bg-purple-100 text-purple-700 border border-purple-200" };
    case ECOMMERCE_ORDER_STATUS.SHORTFALL_AUTO_APPROVED:
      return { label: "Shortfall – Approved", colorClass: "bg-indigo-100 text-indigo-700 border border-indigo-200" };
    case ECOMMERCE_ORDER_STATUS.APPROVED:
      return { label: "Approved",             colorClass: "bg-green-100 text-green-700 border border-green-200" };
    case ECOMMERCE_ORDER_STATUS.IN_TRANSIT:
      return { label: "In Transit",           colorClass: "bg-blue-100 text-blue-700 border border-blue-200" };
    case ECOMMERCE_ORDER_STATUS.DELIVERED:
      return { label: "Delivered",            colorClass: "bg-teal-100 text-teal-700 border border-teal-200" };
    case ECOMMERCE_ORDER_STATUS.DISTRIBUTOR_PENDING:
      return { label: "Distributor Pending",  colorClass: "bg-orange-100 text-orange-700 border border-orange-200" };
    default:
      return { label: `Unknown (${s})`,       colorClass: "bg-gray-100 text-gray-700 border border-gray-200" };
  }
};

const EcommerceOrderList = () => {
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  // Modal states
  const [remarkModal, setRemarkModal] = useState(false);
  const [actionType, setActionType] = useState(""); // Stores "5" for APPROVED or "0" for REJECTED
  const [remark, setRemark] = useState("");
  const [currentOrderId, setCurrentOrderId] = useState(null);

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [CSVData, setCSVData] = useState(null);
  const [fileName, setFileName] = useState(null);

  const [
    bulkAddOrder,
    { isLoading: bulkAddIsLoading }
  ] = useBulkAddEcommerceOrderMutation();

  // Shortfall states
  const [shortfallModal, setShortfallModal] = useState(false);
  const [shortfallOrder, setShortfallOrder] = useState(null);
  const [shortfallLines, setShortfallLines] = useState([]);
  const [shortfallType, setShortfallType] = useState("user_approve");
  const [shortfallRemarks, setShortfallRemarks] = useState("");

  const handleBulkSubmit = async () => {
    if (!CSVData || !CSVData.parsed || CSVData.parsed.length === 0) {
      toast.error("Please upload a valid CSV file first");
      return;
    }

    const requiredColumns = ["Buyer Mobile", "Article No", "Color", "Size", "Item Qty"];
    const headers = CSVData.headers || [];
    const missingColumns = requiredColumns.filter(col => !headers.includes(col));

    if (missingColumns.length > 0) {
      toast.error(`Invalid CSV structure. Missing columns: ${missingColumns.join(", ")}`);
      return;
    }

    toast.loading("Uploading orders...", { id: "bulk-order-upload" });

    try {
      const response = await bulkAddOrder({
        slug: tenant_id,
        token,
        rows: CSVData.parsed,
      }).unwrap();

      toast.dismiss("bulk-order-upload");
      if (response.success) {
        toast.success(response.message || "Bulk order upload completed successfully");
        setShowBulkModal(false);
        setCSVData(null);
        setFileName(null);
        loadOrders();
      } else {
        toast.error(response.message || "Bulk order upload failed");
      }
    } catch (err) {
      toast.dismiss("bulk-order-upload");
      toast.error(err?.data?.message || "Failed to upload bulk orders");
    }
  };

  const navigate = useNavigate();
  const vendorData = JSON.parse(sessionStorage.getItem("vendorData"));
  const tenant_id = vendorData?.tenant_id;
  const token = sessionStorage.getItem("access_token");

  // API Hooks
  const [fetchOrders, { isLoading: isFetchingOrders }] = useFetchEcommerceOrdersMutation();
  const [fetchOrderById, { isLoading: isFetchingDetail }] = useFetchEcommerceOrderByIdMutation();
  const [updateOrder, { isLoading: isUpdating }] = useUpdateEcommerceOrderMutation();
  const [reportShortfall, { isLoading: isReportingShortfall }] = useReportEcommerceShortfallMutation();

  const loadOrders = () => {
    fetchOrders({
      slug: tenant_id,
      token,
      limit: limit.toString(),
      offset: offset.toString(),
      status: statusFilter,
    })
      .unwrap()
      .then((res) => {
        if (res.body) {
          setOrders(res.body?.orderData || []);
          setTotalOrders(res.body?.count || 0);
        }
      })
      .catch((err) => {
        toast.error(err?.data?.message || "Failed to fetch orders");
      });
  };

  useEffect(() => {
    if (tenant_id) {
      loadOrders();
    }
  }, [limit, offset, statusFilter]);

  const handleShowDetails = (order) => {
    const orderId = order.id || order.order_id;
    if (!orderId) {
      toast.error("Order ID is missing in the record");
      return;
    }
    navigate(`${orderId}`);
  };

  const handleOpenShortfallModal = (order) => {
    const orderId = order.id || order.order_id || order.order_no;
    if (!orderId) {
      toast.error("Order ID is missing");
      return;
    }
    setShortfallOrder(order);
    setShortfallLines([]);
    setShortfallRemarks("");
    setShortfallType("user_approve");
    setShortfallModal(true);

    fetchOrderById({
      slug: tenant_id,
      token,
      order_id: orderId.toString(),
      status: order.status,
    })
      .unwrap()
      .then((res) => {
        const orderData = res?.body?.orderData || res?.orderData || (Array.isArray(res) ? res : []);
        if (orderData && orderData.length > 0) {
          const lines = orderData.map((line) => {
            const qty = Number(line.order_qty || 0);
            const amt = Number(line.order_purchase_amount || 0);
            const unitPrice = qty > 0 ? amt / qty : 0;
            return {
              ...line,
              newQty: qty,
              unitPrice: unitPrice,
              newAmount: amt,
            };
          });
          setShortfallLines(lines);
        } else {
          setShortfallModal(false);
          toast.error("Failed to load order lines for editing");
        }
      })
      .catch((err) => {
        setShortfallModal(false);
        toast.error(err?.data?.message || "Failed to load order lines");
      });
  };

  const handleQtyChange = (index, value) => {
    const updated = [...shortfallLines];
    const qty = Math.max(0, parseInt(value) || 0);
    updated[index].newQty = qty;
    updated[index].newAmount = Number((qty * updated[index].unitPrice).toFixed(2));
    setShortfallLines(updated);
  };

  const handleSubmitShortfall = () => {
    if (!shortfallRemarks) {
      toast.error("Please provide remarks for reporting shortfall");
      return;
    }

    const orderId = shortfallOrder?.id || shortfallOrder?.order_id || shortfallOrder?.order_no;
    if (!orderId) {
      toast.error("Order ID is missing");
      return;
    }

    const updatedLinesPayload = shortfallLines.map((line) => ({
      article_no: line.article_no,
      qty: line.newQty,
      purchase_amount: line.newAmount,
    }));

    reportShortfall({
      slug: tenant_id,
      token,
      order_id: Number(orderId),
      shortfall_type: shortfallType,
      remarks: shortfallRemarks,
      updated_lines: updatedLinesPayload,
    })
      .unwrap()
      .then(() => {
        toast.success("Shortfall reported successfully");
        setShortfallModal(false);
        loadOrders();
      })
      .catch((err) => {
        toast.error(err?.data?.message || "Failed to report shortfall");
      });
  };

  const openRemarkModal = (orderId, type) => {
    setCurrentOrderId(orderId);
    setActionType(type);
    setRemark("");
    setRemarkModal(true);
  };

  const handleUpdateStatus = () => {
    if (actionType === ECOMMERCE_ORDER_STATUS.REJECTED && !remark) {
      toast.error("Please provide a remark for rejection");
      return;
    }

    updateOrder({
      slug: tenant_id,
      token,
      order_id: currentOrderId.toString(),
      status: actionType === ECOMMERCE_ORDER_STATUS.REJECTED ? "REJECTED" : "APPROVED",
      remark: remark,
    })
      .unwrap()
      .then((res) => {
        toast.success(`Order ${actionType === ECOMMERCE_ORDER_STATUS.APPROVED ? "approved" : "rejected"} successfully`);
        setRemarkModal(false);
        loadOrders();
      })
      .catch((err) => {
        toast.error(err?.data?.message || "Failed to update order");
      });
  };

  const columns = [
    {
      name: "S.No",
      cell: (row, index) => offset + index + 1
    },
    {
      name: "Order ID",
      selector: (row) => row.id || "N/A",
      sortable: true
    },
    {
      name: "Customer Name",
      selector: (row) => row.user_name || "N/A",
      sortable: true
    },
    {
      name: "Customer Mobile",
      selector: (row) => row.user_mobile || "N/A",
      sortable: true
    },
    {
      name: "User Type",
      selector: (row) => row.user_type || "N/A",
      sortable: true
    },
    {
      name: "Date",
      selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : "N/A"),
      sortable: true
    },
    {
      name: "Quantity",
      selector: (row) => (row.qty ?? "N/A"),
      sortable: true
    },
    {
      name: "Total Amount",
      selector: (row) => (row.bill_value ? `₹${row.bill_value}` : "N/A"),
      sortable: true
    },
    {
      name: "Exp. Delivery Date",
      selector: (row) => (row.expected_delivery ? new Date(row.expected_delivery).toLocaleDateString() : "N/A"),
      sortable: true
    },
    {
      name: "Order Date",
      selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : "N/A"),
      sortable: true
    },
    {
      name: "Status",
      cell: (row) => {
        const { label, colorClass } = getStatusInfo(row.status);
        return (
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${colorClass} whitespace-nowrap`}>
            {label}
          </span>
        );
      },
      width: "160px"
    },
    {
      name: "Actions",
      cell: (row) => {
        const isPending = row.status?.toString() === ECOMMERCE_ORDER_STATUS.PENDING;
        const isUnmapped = !row.seller_app_user_id || Number(row.seller_app_user_id) === 0;

        return (
          <div className="flex space-x-2">
            <button
              onClick={() => handleShowDetails(row)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
              title="View Details"
            >
              <FiEye size={18} />
            </button>
            {/* {isPending && isUnmapped && (
              <>
                <button
                  onClick={() => openRemarkModal(row.id, ECOMMERCE_ORDER_STATUS.APPROVED)}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                  title="Approve"
                >
                  <FiCheckCircle size={18} />
                </button>
                <button
                  onClick={() => openRemarkModal(row.id, ECOMMERCE_ORDER_STATUS.REJECTED)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                  title="Reject"
                >
                  <FiXCircle size={18} />
                </button>
                <button
                  onClick={() => handleOpenShortfallModal(row)}
                  className="p-2 text-purple-600 hover:bg-purple-50 rounded-full transition-colors"
                  title="Report Shortfall"
                >
                  <FiEdit size={18} />
                </button>
              </>
            )} */}
          </div>
        );
      },
      width: "70px"
    },
  ];

  const orderLineColumns = [
    {
      name: "Article No",
      selector: (row) => row.article_no || "N/A",
      width: "150px",
    },
    {
      name: "Color",
      selector: (row) => row.color || "N/A",
    },
    {
      name: "Size",
      selector: (row) => row.size || "N/A",
    },
    {
      name: "Qty",
      selector: (row) => row.order_qty || 0,
    },
    {
      name: "Bill Value",
      selector: (row) => (row.order_purchase_amount ? `₹${row.order_purchase_amount}` : "N/A"),
    }
  ];

  return (
    <>
      <div className="flex flex-col w-full bg-gray-100 min-h-screen">
        <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Ecommerce Order List</h1>
              <LinksComponent />
            </div>
            <div>
              <button
                onClick={() => setShowBulkModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center gap-1 shadow-sm"
              >
                Bulk Upload Orders
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Filters */}
          <div className="bg-white p-4 rounded-xl shadow-md border border-gray-100 mb-6 flex flex-wrap gap-6 items-end">
            <div className="w-64">
              <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Status Filter</label>
              <select
                className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-sm"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="">All Statuses</option>
                <option value={ECOMMERCE_ORDER_STATUS.PENDING}>Pending</option>
                <option value={ECOMMERCE_ORDER_STATUS.SHORTFALL_AWAITING_USER}>Shortfall Awaiting User</option>
                <option value={ECOMMERCE_ORDER_STATUS.SHORTFALL_AUTO_APPROVED}>Shortfall Auto Approved</option>
                <option value={ECOMMERCE_ORDER_STATUS.APPROVED}>Approved</option>
                <option value={ECOMMERCE_ORDER_STATUS.IN_TRANSIT}>In Transit</option>
                <option value={ECOMMERCE_ORDER_STATUS.DELIVERED}>Delivered</option>
                <option value={ECOMMERCE_ORDER_STATUS.REJECTED}>Rejected</option>
                <option value={ECOMMERCE_ORDER_STATUS.DISTRIBUTOR_PENDING}>Distributor Pending</option>
              </select>
            </div>
            <button
              onClick={() => {
                setStatusFilter("");
                setOffset(0);
              }}
              className="px-5 py-2 text-sm font-bold text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-all"
            >
              Clear Filters
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden relative">
            <DataTableComponent
              columns={columns}
              data={orders}
              loading={isFetchingOrders}
              totalRows={totalOrders}
              handlePageChange={(page) => setOffset((page - 1) * limit)}
              handlePerRowsChange={(newSize, page) => {
                setLimit(newSize);
                setOffset((page - 1) * newSize);
              }}
            />
          </div>
        </div>

        {/* Shortfall Edit Modal */}
        <ModalComponent
          show={shortfallModal}
          setShow={(val) => setShortfallModal(val)}
          size="3xl"
        >
          <div className="p-8 max-h-[85vh] flex flex-col">
            <div className="flex-shrink-0 border-b border-gray-100 pb-6 mb-6">
              <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight">Report Order Shortfall</h3>
              <p className="text-sm font-medium text-gray-500 mt-1">
                Modify item quantities to report a stock shortfall.
              </p>
            </div>

            {/* Scrollable Items Form */}
            <div className="flex-grow overflow-y-auto custom-scrollbar pr-1 mb-6">
              {shortfallLines.length > 0 ? (
                <div className="space-y-4">
                  {shortfallLines.map((line, idx) => (
                    <div key={line.id || idx} className="bg-gray-50 p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-black text-gray-800 uppercase tracking-wider">{line.article_no || "N/A"}</p>
                        <div className="flex gap-4 mt-1 text-xs text-gray-500 font-semibold">
                          <span>Color: {line.color || "N/A"}</span>
                          <span>Size: {line.size || "N/A"}</span>
                          <span>Unit Price: ₹{Number(line.unitPrice || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider">Original Qty</span>
                          <span className="text-sm font-bold text-gray-700">{line.order_qty || 0}</span>
                        </div>
                        
                        <div className="w-32">
                          <label className="block text-[10px] text-gray-400 font-black uppercase tracking-wider mb-1">New Qty</label>
                          <input
                            type="number"
                            min="0"
                            className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm font-bold text-gray-800"
                            value={line.newQty}
                            onChange={(e) => handleQtyChange(idx, e.target.value)}
                          />
                        </div>

                        <div className="text-right w-28">
                          <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider">New Total</span>
                          <span className="text-sm font-black text-blue-600">₹{Number(line.newAmount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex justify-center items-center py-10">
                  <CircleLoaderComponent />
                </div>
              )}
            </div>

            {/* Shortfall Type and Remarks */}
            <div className="flex-shrink-0 border-t border-gray-100 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="md:col-span-1">
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Shortfall Approval Type</label>
                  <select
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all text-sm font-bold text-gray-700"
                    value={shortfallType}
                    onChange={(e) => setShortfallType(e.target.value)}
                  >
                    <option value="user_approve">Awaiting User (Retailer)</option>
                    <option value="auto_approve">Auto Approve Flow</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Remarks / Reason</label>
                  <textarea
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all text-sm font-medium min-h-[60px]"
                    placeholder="Provide reasons for the shortfall adjustments..."
                    value={shortfallRemarks}
                    onChange={(e) => setShortfallRemarks(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShortfallModal(false)}
                  className="px-6 py-3 text-sm font-bold text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all uppercase tracking-widest"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitShortfall}
                  disabled={isReportingShortfall || shortfallLines.length === 0}
                  className="px-8 py-3 text-sm font-black text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-all shadow-lg active:scale-95 uppercase tracking-widest shadow-purple-100"
                >
                  {isReportingShortfall ? "Submitting..." : "Submit Shortfall"}
                </button>
              </div>
            </div>
          </div>
        </ModalComponent>

        {/* Remark / Action Modal */}
        <ModalComponent
          show={remarkModal}
          setShow={(val) => setRemarkModal(val)}
          size="md"
        >
          <div className="p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className={`p-4 rounded-2xl ${actionType === ECOMMERCE_ORDER_STATUS.APPROVED ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
                {actionType === ECOMMERCE_ORDER_STATUS.APPROVED ? <FiCheckCircle size={32} /> : <FiXCircle size={32} />}
              </div>
              <div>
                <h2 className="text-2xl font-black text-gray-900">
                  {actionType === ECOMMERCE_ORDER_STATUS.APPROVED ? "Approve Order" : "Reject Order"}
                </h2>
                <p className="text-sm font-medium text-gray-500">Confirm your final decision.</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-6 font-medium leading-relaxed">
              Are you sure you want to <span className="font-bold">{actionType === ECOMMERCE_ORDER_STATUS.APPROVED ? "approve" : "reject"}</span> this order?
            </p>

            <div className="mb-8">
              <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Decision Remark</label>
              <textarea
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all text-sm font-medium min-h-[100px]"
                placeholder="Enter your notes here..."
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              ></textarea>
            </div>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setRemarkModal(false)}
                className="px-6 py-3 text-sm font-bold text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all uppercase tracking-widest"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateStatus}
                disabled={isUpdating}
                className={`px-8 py-3 text-sm font-black text-white rounded-xl transition-all shadow-lg active:scale-95 uppercase tracking-widest ${actionType === ECOMMERCE_ORDER_STATUS.APPROVED
                    ? "bg-green-600 hover:bg-green-700 shadow-green-100"
                    : "bg-red-600 hover:bg-red-700 shadow-red-100"
                  }`}
              >
                {isUpdating ? "Processing..." : actionType === ECOMMERCE_ORDER_STATUS.APPROVED ? "Confirm Approval" : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </ModalComponent>

        {/* Bulk Upload Orders Modal */}
        <ModalComponent
          show={showBulkModal}
          setShow={setShowBulkModal}
          size="lg"
        >
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">Bulk Upload Orders</h2>
            
            <p className="text-sm text-gray-600 mb-6 text-center leading-relaxed">
              Upload your orders in bulk using a CSV file. Rows with identical Buyer Mobile and Order Remark will be grouped into a single order with multiple line items.
            </p>

            <div className="mb-6 flex justify-center">
              <button
                onClick={() => downloadCSV(sampleOrdersData, "sample_ecommerce_orders.csv")}
                className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 text-sm border border-blue-200 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                Download Sample CSV Template
              </button>
            </div>

            <div className="mb-6 border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
              <CSVReader
                setCSVData={setCSVData}
                setFileName={setFileName}
              />
              {fileName && (
                <div className="mt-3 text-center text-sm font-medium text-green-700 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200 inline-block mx-auto">
                  Selected File: {fileName}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowBulkModal(false);
                  setCSVData(null);
                  setFileName(null);
                }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-gray-200"
                disabled={bulkAddIsLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkSubmit}
                className={`bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm active:scale-95 ${
                  bulkAddIsLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={bulkAddIsLoading}
              >
                {bulkAddIsLoading ? "Uploading..." : "Submit File"}
              </button>
            </div>
          </div>
        </ModalComponent>
      </div>
    </>
  );
};

export default EcommerceOrderList;
